import { isTurnComplete, isAwaitingExternalTool, isAwaitingApproval, isAwaitingToolResult, extractToolUseBlocks, buildToolResults, findLastAssistantMessage, getToolUseIdForResult, collectResolvedToolUseIds } from '../utils/chatHelpers';
import { extractToolApprovalEntries } from '../utils/toolApprovalHelpers';
import { convertFieldValueAvroToJson } from 'rpc-client-utils';
export const RETRY_DELAYS = [500, 1000, 2000];

// Detects the CHIRP INVALID_REQUEST error that sendToolResponse/sendToolApproval
// return from a resume. Two server conditions produce it and they are
// indistinguishable at the error level — both `BreezeEngineError.invalidRequest()`:
//   1. the turn has not transitioned to AWAITING_TOOL_RESULT/APPROVAL yet — a
//      benign race that should be retried, and
//   2. (post BreezeEngine #2536) the compare-and-set duplicate-resume rejection,
//      which must not be retried.
// The outer CHIRP error wraps a ChirpError in `cause` (with BreezeEngineError
// fields, including `errorType`, spread onto it), and stream errors are mapped to
// a ChatError whose own `type` is the errorType — so check every layer.
export function isInvalidRequestError(err) {
  if (!err || typeof err !== 'object') return false;
  const asObj = err;
  if (asObj.errorType === 'INVALID_REQUEST' || asObj.type === 'INVALID_REQUEST') {
    return true;
  }
  const cause = asObj.cause;
  if (cause != null && typeof cause === 'object') {
    const causeObj = cause;
    if (causeObj.errorType === 'INVALID_REQUEST') return true;
    const inner = causeObj.cause;
    if (inner != null && typeof inner === 'object' && inner.errorType === 'INVALID_REQUEST') {
      return true;
    }
  }
  return false;
}
export function isTransientError(err) {
  if (!err || typeof err !== 'object') return false;
  const asObj = err;
  const status = asObj.status;
  if (typeof status === 'number') {
    return status === 400 || status >= 500;
  }
  // CHIRP errors: the outer error wraps a ChirpError in `cause`.
  // ChirpError has BreezeEngineError fields (including `shouldRetry`) spread
  // directly onto it by spreadPropertiesIntoError in rpc-client-utils.
  // BreezeEngineError has no HTTP status field, so we check errorType directly.
  // INVALID_REQUEST is treated as transient because a resume can hit the benign
  // "turn not AWAITING_TOOL_RESULT yet" race. The compare-and-set duplicate
  // rejection (BreezeEngine #2536) shares this errorType and would be retried
  // here too, but the in-flight lock in doSendToolResponse/resolveToolApproval
  // keeps the client from firing that duplicate in the first place, and
  // sendResumeWithRetry swallows any INVALID_REQUEST that still surfaces so it
  // never becomes a user-facing error.
  if (isInvalidRequestError(err)) {
    return true;
  }
  const cause = asObj.cause;
  if (cause != null && typeof cause === 'object') {
    const causeObj = cause;
    const causeStatus = causeObj.status;
    if (typeof causeStatus === 'number') {
      return causeStatus === 400 || causeStatus >= 500;
    }
    if (typeof causeObj.shouldRetry === 'boolean') {
      return causeObj.shouldRetry;
    }
  }
  const message = asObj.message;
  if (typeof message === 'string') {
    return /\b(400|500|502|503|504)\b/.test(message);
  }
  return false;
}
function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}
export async function withRetry(fn, delays = RETRY_DELAYS) {
  for (let attempt = 0; attempt <= delays.length; attempt++) {
    try {
      return await fn();
    } catch (err) {
      const hasRetriesLeft = attempt < delays.length;
      if (hasRetriesLeft && isTransientError(err)) {
        await delay(delays[attempt]);
        continue;
      }
      throw err;
    }
  }
  throw new Error('withRetry: unreachable');
}
function formatRpcError(rpcError) {
  const {
    errorType,
    errorMessage
  } = rpcError;
  if (typeof errorMessage !== 'string') return null;
  if (typeof errorType === 'string') return `${errorType}: ${errorMessage}`;
  return errorMessage;
}

// Local CHIRP tool calls reject with a doubly-wrapped error from rpc-client-utils:
// makeChirpAvroCall re-throws a generic "CHIRP RPC failed for X" error whose
// .cause is a ChirpError, and the raw rpcError payload (with errorType/errorMessage)
// sits at .cause.cause. Unwrap it so the agent sees the real RPC error rather than
// the generic outer message.
function unwrapChirpToolError(err) {
  if (!(err instanceof Error)) return 'Tool execution failed';
  const cause = err.cause;
  if (!cause || typeof cause !== 'object') return err.message;
  const inner = cause.cause;
  if (inner && typeof inner === 'object') {
    const formatted = formatRpcError(inner);
    if (formatted !== null) return formatted;
  }
  const formattedCause = formatRpcError(cause);
  if (formattedCause !== null) return formattedCause;
  return err.message;
}
function selectApprovalBlocks(toolUseBlocks, approvalDescriptions, approvalPreviews) {
  const approvalRequiredBlocks = toolUseBlocks.filter(block => block.approvalRequired);
  if (approvalRequiredBlocks.length > 0) return approvalRequiredBlocks;
  const approvalToolUseIds = new Set([...approvalDescriptions.keys(), ...approvalPreviews.keys()]);
  if (approvalToolUseIds.size > 0) {
    return toolUseBlocks.filter(block => approvalToolUseIds.has(getToolUseIdForResult(block)));
  }
  return toolUseBlocks;
}
export function createToolResolver(ctx, getToolMap, registry, retryDelays) {
  const dispatchedToolUseIds = new Set();

  // Coalesces concurrent submits of the same resume. Keyed by conversationId +
  // turnId + the sorted tool-use ids so each distinct tool round gets its own
  // entry (a multi-round turn keeps the same turnId), while a duplicate submit of
  // one round — a re-run reducer, a re-delivered Ably stop-reason, a
  // slow/streaming resume, or a double click — coalesces onto the in-flight send
  // instead of firing a second RPC that the backend would execute as a duplicate
  // turn. Additive to the existing per-tool dedupe (dispatchedToolUseIds /
  // resolvedToolUseIds); this serializes the RPC send itself.
  const inFlightResumes = new Map();

  // `kind` keeps the approval and tool-response resumes for the same tool in
  // separate entries. An approved client-side tool produces both an approval
  // resume and a follow-up tool-response resume with the identical tool-use id;
  // without the discriminator they collide, and on a persistent channel the
  // tool-response send would coalesce onto the approval send that is still
  // parked awaiting the very continuation that drives it — a deadlock.
  function resumeKey(kind, conversationId, turnId, toolUseIds) {
    return `${kind}:${conversationId}:${turnId}:${[...toolUseIds].sort().join(',')}`;
  }

  // Sends a resume with the existing not-ready-yet retry, then swallows a benign
  // INVALID_REQUEST so it never becomes a user-facing error. Post-#2536 an
  // INVALID_REQUEST is either the "turn not AWAITING_TOOL_RESULT yet" race
  // (already retried by withRetry) or the server's compare-and-set duplicate-
  // resume rejection — indistinguishable at the error level and both benign,
  // since the winning resume drives the turn. Any other failure propagates.
  async function sendResumeWithRetry(send) {
    try {
      await withRetry(send, retryDelays);
    } catch (err) {
      if (isInvalidRequestError(err)) {
        ctx.telemetry.resumeRejectionSuppressed();
        return;
      }
      throw err;
    }
  }
  async function doSendToolResponse(toolResults) {
    const state = ctx.getState();
    if (state.conversationId === null || state.turnId === null) return;
    const cId = state.conversationId;
    const tId = state.turnId;
    const key = resumeKey('response', cId, tId, toolResults.map(result => result.toolUseId));
    const inFlight = inFlightResumes.get(key);
    if (inFlight) {
      // A submit for this exact resume is already in flight; coalesce onto it
      // rather than firing a duplicate RPC. Swallow its outcome — the original
      // caller owns error reporting, so a duplicate never surfaces its own error.
      await inFlight.catch(() => {});
      return;
    }
    ctx.dispatch({
      type: 'TOOL_CLEAR'
    });
    ctx.dispatch({
      type: 'STATUS_SET',
      status: ctx.transport.activeStatus
    });
    const sendPromise = sendResumeWithRetry(() => ctx.transport.sendToolResponse(Object.assign({
      conversationId: cId,
      turnId: tId,
      toolResults,
      externalToolSpecs: ctx.getMergedToolSpecs(),
      externalSkillSpecs: ctx.getExternalSkillSpecs(),
      externalSubagentSpecs: ctx.getExternalSubagentSpecs().length > 0 ? ctx.getExternalSubagentSpecs() : undefined,
      externalArtifactPluginSpecs: ctx.getExternalArtifactPluginSpecs().length > 0 ? ctx.getExternalArtifactPluginSpecs() : undefined
    }, ctx.getResumeMetadata(), ctx.getResumeContexts()), ctx.createHandler()));
    inFlightResumes.set(key, sendPromise);
    try {
      await sendPromise;
    } finally {
      inFlightResumes.delete(key);
    }
  }
  async function checkAndSendToolResponse() {
    const state = ctx.getState();
    if (state.cancelled) return;
    // Nothing to submit. Guards the re-entry where a second call races in after
    // the first send already dispatched TOOL_CLEAR: without this, the empty
    // `size (0) < length (0)` check below is false and we would fire a second,
    // empty sendToolResponse for the same resume.
    if (state.activeTools.length === 0) return;
    if (state.pendingToolResults.size < state.activeTools.length) return;
    await doSendToolResponse(buildToolResults(state.pendingToolResults, state.activeTools));
  }
  async function executeAutoTool(toolUseId, tool, input) {
    ctx.logger.toolExecutionStart(toolUseId, tool.name, input);
    ctx.telemetry.toolExecutionStarted(toolUseId);
    try {
      const result = await tool.execute(input);
      if (ctx.getState().cancelled) return;
      const toolResult = {
        content: result
      };
      ctx.dispatch({
        type: 'TOOL_RESULT_SET',
        toolUseId,
        result: toolResult,
        status: 'complete'
      });
      ctx.logger.toolExecutionEnd(toolUseId, tool.name, result, 'complete');
      ctx.telemetry.toolExecutionFinished(toolUseId, tool.name, 'complete');
    } catch (err) {
      if (ctx.getState().cancelled) return;
      const errorMessage = unwrapChirpToolError(err);
      const errorResult = {
        content: errorMessage
      };
      ctx.dispatch({
        type: 'TOOL_RESULT_SET',
        toolUseId,
        result: errorResult,
        status: 'error'
      });
      ctx.logger.toolExecutionEnd(toolUseId, tool.name, errorMessage, 'error');
      ctx.telemetry.toolExecutionFinished(toolUseId, tool.name, 'error');
    }
  }
  async function executeRegistryTool(toolUseId, toolName, input) {
    ctx.logger.toolExecutionStart(toolUseId, toolName, input);
    ctx.telemetry.toolExecutionStarted(toolUseId);
    ctx.dispatch({
      type: 'TOOL_STATUS_UPDATED',
      toolUseId,
      update: {
        status: 'executing'
      }
    });
    try {
      const result = await registry.executeTool(toolName, input, toolUseId);
      if (ctx.getState().cancelled) return;
      const resultContent = typeof result === 'string' ? result : JSON.stringify(result);
      ctx.dispatch({
        type: 'TOOL_RESULT_SET',
        toolUseId,
        result: {
          content: resultContent
        },
        status: 'complete'
      });
      ctx.logger.toolExecutionEnd(toolUseId, toolName, resultContent, 'complete');
      ctx.telemetry.toolExecutionFinished(toolUseId, toolName, 'complete');
    } catch (err) {
      if (ctx.getState().cancelled) return;
      const errorMessage = unwrapChirpToolError(err);
      ctx.dispatch({
        type: 'TOOL_RESULT_SET',
        toolUseId,
        result: {
          type: 'TOOL_ERROR',
          message: errorMessage
        },
        status: 'error'
      });
      ctx.logger.toolExecutionEnd(toolUseId, toolName, errorMessage, 'error');
      ctx.telemetry.toolExecutionFinished(toolUseId, toolName, 'error');
    }
  }

  // On persistent-channel transports (Ably) the server continues streaming after
  // tool calls, so don't mark the turn complete. On polling/streaming the channel
  // is dead and completing is the right fallback.
  // suppressDeadChannelComplete (streaming replay): handleExternalTool takes a
  // separate path that calls checkAndSendToolResponse instead, so this function
  // is not called in that case.
  async function completeIfDeadChannel() {
    if (!ctx.transport.isPersistentChannel && !ctx.transport.suppressDeadChannelComplete) {
      await completeTurn();
    }
  }
  async function handleExternalTool() {
    // Guard against re-entry from polling while a tool is already executing.
    // The polling transport can fire handleExternalTool multiple times before
    // the async tool finishes. Without this guard, each re-entry dispatches
    // TOOL_USE_DETECTED (resetting pendingToolResults and activeTools) and
    // launches another executeRegistryTool, so subsequent TOOL_RESULT_SET
    // dispatches land against stale/empty activeTools — producing toolName: "".
    if (ctx.getState().activeTools.some(t => t.status === 'executing')) {
      return;
    }
    const extracted = extractToolUseBlocks(ctx.getState().messages);
    if (!extracted) {
      await completeIfDeadChannel();
      return;
    }
    const {
      toolUseBlocks
    } = extracted;

    // Only handle tools the client knows about. Tools absent from both the
    // toolMap and the registry are executed server-side.
    const clientSideBlocks = toolUseBlocks.filter(block => getToolMap().has(block.name) || registry.hasTool(block.name));
    if (clientSideBlocks.length === 0) {
      if (ctx.transport.suppressDeadChannelComplete) {
        // In streaming replay mode, tool state is pre-injected into the reducer
        // via onDispatchNeeded. Call checkAndSendToolResponse to advance to the
        // next recorded batch if all results are ready.
        await checkAndSendToolResponse();
      } else {
        await completeIfDeadChannel();
      }
      return;
    }

    // Skip any block whose tool response was already sent. Two cases:
    //   1. dispatchedToolUseIds — the Ably transport can re-deliver the same
    //      stop-reason event (at-least-once delivery) after TOOL_CLEAR resets
    //      activeTools, within this client instance.
    //   2. resolvedToolUseIds — on resume/reload the tool result already lives
    //      in the persisted history (the original page sent it and the backend
    //      recorded it). A fresh client instance has an empty
    //      dispatchedToolUseIds, so without this the replayed EXTERNAL_TOOL
    //      would re-execute the tool and re-send a duplicate tool response —
    //      which the backend rejects with 400 INVALID_REQUEST.
    const resolvedToolUseIds = collectResolvedToolUseIds(ctx.getState().messages);
    const newBlocks = clientSideBlocks.filter(block => {
      const toolUseId = getToolUseIdForResult(block);
      return !dispatchedToolUseIds.has(toolUseId) && !resolvedToolUseIds.has(toolUseId);
    });
    if (newBlocks.length === 0) {
      return;
    }
    for (const block of newBlocks) {
      dispatchedToolUseIds.add(getToolUseIdForResult(block));
    }
    ctx.dispatch({
      type: 'TOOL_USE_DETECTED',
      blocks: newBlocks
    });
    const autoRunPromises = [];
    for (const block of newBlocks) {
      const tool = getToolMap().get(block.name);
      const toolUseId = getToolUseIdForResult(block);
      if (!tool) {
        autoRunPromises.push(executeRegistryTool(toolUseId, block.name, convertFieldValueAvroToJson(block.input)));
        continue;
      }
      if (tool.execute) {
        ctx.dispatch({
          type: 'TOOL_STATUS_UPDATED',
          toolUseId,
          update: {
            status: 'executing'
          }
        });
        autoRunPromises.push(executeAutoTool(toolUseId, tool, convertFieldValueAvroToJson(block.input)));
      }
    }
    if (autoRunPromises.length > 0) {
      await Promise.all(autoRunPromises);
    }
    await checkAndSendToolResponse();
  }
  let pendingApprovalDeferred = null;
  async function handleToolApproval() {
    const extracted = extractToolUseBlocks(ctx.getState().messages);
    if (!extracted) {
      await completeTurn();
      return;
    }
    const currentMessages = ctx.getState().messages;
    const {
      descriptions: approvalDescriptions,
      previews: approvalPreviews
    } = extractToolApprovalEntries(currentMessages);
    const approvalBlocks = selectApprovalBlocks(extracted.toolUseBlocks, approvalDescriptions, approvalPreviews);
    ctx.dispatch(Object.assign({
      type: 'TOOL_APPROVAL_REQUIRED',
      blocks: approvalBlocks
    }, approvalDescriptions.size > 0 && {
      approvalDescriptions
    }, approvalPreviews.size > 0 && {
      approvalPreviews
    }));
    await new Promise((resolve, reject) => {
      pendingApprovalDeferred = {
        resolve,
        reject
      };
    });
  }
  async function completeTurn() {
    if (ctx.onBeforeTurnComplete) {
      try {
        const state = ctx.getState();
        await ctx.onBeforeTurnComplete({
          conversationId: state.conversationId,
          turnId: state.turnId
        });
      } catch (_unused) {
        // A failed prefetch must not strand the conversation in STREAMING.
      }
    }
    ctx.dispatch({
      type: 'TURN_COMPLETED'
    });
    if (ctx.onAfterTurnComplete) {
      var _findLastAssistantMes;
      const state = ctx.getState();
      ctx.onAfterTurnComplete({
        conversationId: state.conversationId,
        turnId: state.turnId,
        stopReason: (_findLastAssistantMes = findLastAssistantMessage(state.messages)) === null || _findLastAssistantMes === void 0 ? void 0 : _findLastAssistantMes.stopReason
      });
    }
  }
  async function handleStopReason(stopReason) {
    if (stopReason === 'ERROR') {
      ctx.dispatch({
        type: 'TURN_ERROR',
        error: {
          type: 'UNKNOWN',
          message: 'Something went wrong. Please try again.'
        }
      });
      return;
    }
    if (isTurnComplete(stopReason)) {
      await completeTurn();
      return;
    }
    if (isAwaitingExternalTool(stopReason)) {
      await handleExternalTool();
      return;
    }
    if (isAwaitingApproval(stopReason)) {
      await handleToolApproval();
      return;
    }
    if (isAwaitingToolResult(stopReason)) {
      const lastMsg = findLastAssistantMessage(ctx.getState().messages);
      if (!(lastMsg !== null && lastMsg !== void 0 && lastMsg.stopReason)) {
        ctx.dispatch({
          type: 'TURN_ERROR',
          error: {
            type: 'UNKNOWN',
            message: 'Server requires tool result but no stop reason found'
          }
        });
        return;
      }
      if (lastMsg.stopReason === 'TOOLS_REQUIRE_APPROVAL') {
        await handleToolApproval();
      } else {
        await handleExternalTool();
      }
      return;
    }
    await completeTurn();
  }
  async function resolveToolApproval(toolUseId, approved, denialFeedback, alwaysAllow) {
    const state = ctx.getState();
    if (state.status !== 'AWAITING_APPROVAL') return;
    if (!state.activeTools.some(tool => tool.toolUseId === toolUseId)) return;
    ctx.dispatch({
      type: 'TOOL_APPROVAL_SET',
      toolUseId,
      approved,
      denialFeedback,
      alwaysAllow
    });
    const nextState = ctx.getState();
    if (nextState.pendingApprovals.size < nextState.activeTools.length) return;
    const conversationId = nextState.conversationId;
    const turnId = nextState.turnId;
    if (conversationId === null || turnId === null) return;
    const key = resumeKey('approval', conversationId, turnId, [...nextState.pendingApprovals.keys()]);
    const inFlight = inFlightResumes.get(key);
    if (inFlight) {
      // A submit for this approval resume is already in flight; coalesce onto it
      // rather than firing a duplicate sendToolApproval RPC. Still resolve this
      // call's approval wait: the in-flight owner resolves only the deferred it
      // captured, so a re-armed handleToolApproval would otherwise hang here.
      const pending = pendingApprovalDeferred;
      pendingApprovalDeferred = null;
      await inFlight.catch(() => {});
      pending === null || pending === void 0 || pending.resolve();
      return;
    }
    ctx.dispatch({
      type: 'TOOL_CLEAR'
    });
    ctx.dispatch({
      type: 'STATUS_SET',
      status: ctx.transport.activeStatus
    });
    const deferred = pendingApprovalDeferred;
    pendingApprovalDeferred = null;
    const approvals = [...nextState.pendingApprovals.values()];
    const sendPromise = (async () => {
      try {
        await ctx.transport.sendToolApproval(Object.assign({
          conversationId,
          turnId,
          approvals
        }, ctx.getResumeMetadata(), ctx.getResumeContexts()), ctx.createHandler());
      } catch (err) {
        // Approvals are best-effort: a failed submit — including the server's
        // compare-and-set duplicate-resume INVALID_REQUEST rejection — must not
        // surface a user-facing error.
        if (isInvalidRequestError(err)) {
          ctx.telemetry.resumeRejectionSuppressed();
        }
      }
    })();
    inFlightResumes.set(key, sendPromise);
    try {
      await sendPromise;
    } finally {
      inFlightResumes.delete(key);
      deferred === null || deferred === void 0 || deferred.resolve();
    }
  }
  function cancelPendingApproval() {
    const deferred = pendingApprovalDeferred;
    pendingApprovalDeferred = null;
    deferred === null || deferred === void 0 || deferred.resolve();
  }
  return {
    handleStopReason,
    handleExternalTool,
    handleToolApproval,
    checkAndSendToolResponse,
    resolveToolApproval,
    cancelPendingApproval
  };
}